import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

export default function Hero() {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const trigger = 320;
      const next = Math.min(Math.max(window.scrollY / trigger, 0), 1);
      setProgress(next);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const sidePadding = progress * 20;
  const topPadding = progress * 20;
  const radius = progress * 28;
  const heightVh = 100 - progress * 12;
  const minHeight = 640;

  return (
    <section
      className="relative"
      style={{
        paddingLeft: `${sidePadding}px`,
        paddingRight: `${sidePadding}px`,
        paddingTop: `${topPadding}px`,
      }}
    >
      <div
        className="relative overflow-hidden transition-[border-radius] duration-200 ease-out"
        style={{
          height: `${heightVh}vh`,
          minHeight: `${minHeight}px`,
          borderRadius: `${radius}px`,
        }}
      >
        <img
          src="https://readdy.ai/api/search-image?query=Stunning%20editorial%20interior%20of%20a%20sunlit%20modern%20living%20room%20with%20cream%20walls%20linen%20sofa%20wooden%20floor%20and%20olive%20tree%20in%20terracotta%20pot%2C%20golden%20afternoon%20light%20streaming%20through%20sheer%20curtains%20casting%20soft%20long%20shadows%2C%20warm%20neutral%20palette%20serene%20calm%20atmosphere%20premium%20home%20photography%20with%20layered%20textures&width=2000&height=1400&seq=hero-pure-2026&orientation=landscape"
          alt="Sunlit living room"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/35 via-foreground-950/15 to-foreground-950/55"></div>

        <div className="relative z-10 h-full w-full flex flex-col items-center justify-center text-center px-4 md:px-8">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-background-50/40 text-background-50 text-xs tracking-widest uppercase backdrop-blur-sm animate-fade-up">
            <span className="w-1.5 h-1.5 rounded-full bg-accent-400"></span>
            Booking spring slots now
          </span>

          <h1 className="font-heading text-background-50 text-5xl md:text-7xl lg:text-[88px] font-medium leading-[1.02] mt-6 max-w-5xl animate-fade-up">
            A cleaner home,<br />without the choreography.
          </h1>

          <p className="mt-6 text-background-100/90 text-base md:text-lg max-w-xl leading-relaxed animate-fade-up">
            Vetted, insured cleaners. Real-time availability. One tap to rebook. PureNest turns the chore you keep postponing into something you actually look forward to.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row items-center gap-3 animate-fade-up">
            <Link
              to="/booking"
              className="group inline-flex items-center gap-2 bg-background-50 text-foreground-950 pl-6 pr-2 py-2 rounded-full text-sm font-medium whitespace-nowrap hover:bg-background-100 transition-colors"
            >
              Book a clean in 90 seconds
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 group-hover:bg-primary-600 transition-colors">
                <i className="ri-arrow-right-line text-background-50"></i>
              </span>
            </Link>
            <a
              href="#how-it-works"
              className="inline-flex items-center gap-2 text-background-50 text-sm font-medium px-5 py-3 rounded-full border border-background-50/40 hover:bg-background-50/10 whitespace-nowrap transition-colors"
            >
              <i className="ri-play-circle-line text-lg"></i>
              See how it works
            </a>
          </div>

          <div className="mt-14 hidden md:flex items-center gap-10 text-background-50/85 animate-fade-up">
            <div className="flex items-center gap-3">
              <div className="flex -space-x-2">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20close%20up%20portrait%20of%20a%20smiling%20female%20professional%20house%20cleaner%20with%20short%20hair%20against%20soft%20cream%20studio%20background%2C%20editorial%20photography%20natural%20light%20approachable%20friendly&width=120&height=120&seq=hero-stack-a1&orientation=squarish"
                  alt="Cleaner Maya"
                  className="w-9 h-9 rounded-full border-2 border-background-50 object-cover object-top"
                />
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20close%20up%20portrait%20of%20a%20smiling%20male%20professional%20house%20cleaner%20with%20short%20dark%20hair%20against%20soft%20cream%20studio%20background%2C%20editorial%20photography%20natural%20light%20approachable%20friendly&width=120&height=120&seq=hero-stack-b2&orientation=squarish"
                  alt="Cleaner Daniel"
                  className="w-9 h-9 rounded-full border-2 border-background-50 object-cover object-top"
                />
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20close%20up%20portrait%20of%20a%20smiling%20female%20professional%20house%20cleaner%20with%20long%20wavy%20hair%20against%20soft%20cream%20studio%20background%2C%20editorial%20photography%20natural%20light%20approachable%20friendly&width=120&height=120&seq=hero-stack-c3&orientation=squarish"
                  alt="Cleaner Aisha"
                  className="w-9 h-9 rounded-full border-2 border-background-50 object-cover object-top"
                />
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20close%20up%20portrait%20of%20a%20smiling%20male%20professional%20house%20cleaner%20with%20beard%20against%20soft%20cream%20studio%20background%2C%20editorial%20photography%20natural%20light%20approachable%20friendly&width=120&height=120&seq=hero-stack-d4&orientation=squarish"
                  alt="Cleaner Liam"
                  className="w-9 h-9 rounded-full border-2 border-background-50 object-cover object-top"
                />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium">320+ vetted cleaners</p>
                <p className="text-xs text-background-100/75">Background-checked & insured</p>
              </div>
            </div>
            <div className="h-8 w-px bg-background-50/30"></div>
            <div className="flex items-center gap-2">
              <div className="flex text-secondary-400">
                {Array.from({ length: 5 }).map((_, i) => (
                  <i key={i} className="ri-star-fill text-sm"></i>
                ))}
              </div>
              <p className="text-sm">4.9 from 12,400 cleanings</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}